import _ from 'lodash';

import { ILevelProjectData, levelProjectData } from 'src/app/infrastructure/models/level-project-data';
import { IProjectForm } from './project-form';
import { IProjectDto, projectDto } from 'src/assets/common/models/project-dto.g';
import { ContactData, IContactData } from '../../contact/models/contact-data';
import { IProjectCategoryData, ProjectCategoryData } from '../../project-category/models/project-category-data';
import { projectCategoryDto } from 'src/assets/common/models/project-category-dto.g';
import { IImageBoxData, ImageBoxData } from '../../image/models/image-box-data';
import { IImageParentData, imageParentData } from 'src/app/features/image/models/image-parent-data';
import { imageBoxDto } from 'src/assets/common/models/image-box-dto.g';
import { imageDto } from 'src/assets/common/models/image-dto.g';
import { IAutoComplete } from '../../shared/controls/models/auto-complete.model';
import { dayToLocalDate } from 'src/app/infrastructure/functions/zone-day.function';

export interface IProjectCache {
    data: IProjectData;
    url: string;
}

export const projectData = {
    ...levelProjectData,
    ...imageParentData,

    nr: 'nr',
    guid: 'guid',
    name: 'name',
    description: 'description',
    dateStart: 'dateStart',
    dateCreditEnd: 'dateCreditEnd',
    // Eerste dag zonder werk in de zone van het project (backend: DateCloseDay) als lokale Date voor
    // weergave/datepicker; dateCloseDay ('yyyy-MM-dd') voor de datumregels.
    dateClose: 'dateClose',
    dateCloseDay: 'dateCloseDay',
    // Land (autocomplete, itemId = ISO-code) en IANA-tijdzone van het project: standaard die van het
    // Domain, wijzigbaar. De zone bepaalt de kalenderdag van projectlidmaatschappen en DateClose.
    country: 'country',
    countryCode: 'countryCode',
    timeZoneId: 'timeZoneId',
    deleted: 'deleted',
    inactive: 'inactive',

    // Weergave: klantnaam (contact) of anders de vrije omschrijving.
    customer_description: 'customer_description',
    projectCategory_description: 'projectCategory_description',
    // Formulier: categorie en klant als Guid (backend vertaalt naar Id), of een vrije klantomschrijving.
    projectCategory_guid: 'projectCategory_guid',
    customer_guid: 'customer_guid',
    customerDescription: 'customerDescription',
    address: 'address',
    streetNr: 'streetNr',
    postalCode: 'postalCode',
    town: 'town',
    county: 'county',

    photoBoxData: 'photoDto',
    customerData: 'customerData',
    projectCategoryData: 'projectCategoryData',    
} as const;

export type TProjectData = (typeof projectData)[keyof typeof projectData];

export interface IProjectData extends ILevelProjectData, IImageParentData {
    [projectData.guid]: string;
    [projectData.nr]: number | null;
    [projectData.name]: string | null;
    [projectData.description]: string | null;
    [projectData.dateStart]: Date | null;
    [projectData.dateCreditEnd]: Date | null;
    [projectData.dateClose]: Date | null;
    [projectData.dateCloseDay]: string | null;
    [projectData.country]: IAutoComplete | null;
    [projectData.countryCode]: string | null;
    [projectData.timeZoneId]: string | null;
    [projectData.deleted]: boolean;
    [projectData.inactive]: boolean;

    [projectData.customer_description]: string | null;
    [projectData.projectCategory_description]: string | null;
    [projectData.projectCategory_guid]: string | null;
    [projectData.customer_guid]: string | null;
    [projectData.customerDescription]: string | null;
    [projectData.address]: string | null;
    [projectData.streetNr]: string | null;
    [projectData.postalCode]: string | null;
    [projectData.town]: string | null;
    [projectData.county]: string | null;

    [projectData.photoThumbGuid]: string | null;
    [projectData.photoVersion]: string | null;
    [projectData.photoOriginGuid]: string | null;

    [projectData.photoBoxData]?: IImageBoxData | null;
    [projectData.customerData]?: IContactData | null;
    [projectData.projectCategoryData]?: IProjectCategoryData | null;
}

export class ProjectData
    implements IProjectData, ILevelProjectData
{
    [projectData.guid]!: string;
    [projectData.nr]: number | null = null;
    [projectData.name]: string | null = null;
    [projectData.description]: string | null = null;
    [projectData.dateStart]: Date | null = null;
    [projectData.dateCreditEnd]: Date | null = null;
    [projectData.dateClose]: Date | null = null;
    [projectData.dateCloseDay]: string | null = null;
    [projectData.country]: IAutoComplete | null = null;
    [projectData.countryCode]: string | null = null;
    [projectData.timeZoneId]: string | null = null;
    [projectData.deleted]!: boolean;
    [projectData.inactive]!: boolean;

    [projectData.customer_description]: string | null = null;
    [projectData.projectCategory_description]: string | null = null;
    [projectData.projectCategory_guid]: string | null = null;
    [projectData.customer_guid]: string | null = null;
    [projectData.customerDescription]: string | null = null;
    [projectData.address]: string | null = null;
    [projectData.streetNr]: string | null = null;
    [projectData.postalCode]: string | null = null;
    [projectData.town]: string | null = null;
    [projectData.county]: string | null = null;

    [projectData.photoThumbGuid]: string | null = null;
    [projectData.photoVersion]: string | null = null;
    [projectData.photoOriginGuid]: string | null = null;

    [projectData.photoBoxData]?: IImageBoxData | null = null;
    [projectData.customerData]?: IContactData | null = null;
    [projectData.projectCategoryData]?: IProjectCategoryData | null = null;

    protected constructor(modelDto?: IProjectDto | null);
    protected constructor(modelData: IProjectData, modelForm: IProjectForm);
    protected constructor(value?: IProjectDto | IProjectData | null, modelForm?: IProjectForm) {
        if (!!modelForm) {
            const _modelData = value as IProjectData;
            _.assign(this, _modelData, modelForm);
        } else if (!!value) {
            const _modelDto = value as IProjectDto;

            this[projectData.nr] = _modelDto[projectDto.nr];
            this[projectData.guid] = _modelDto[projectDto.guid]!;
            this[projectData.name] = _modelDto[projectDto.name];
            this[projectData.description] = _modelDto[projectDto.description];
            this[projectData.dateStart] = !!_modelDto[projectDto.dateStart] ? new Date(_modelDto[projectDto.dateStart]!) : null;
            this[projectData.dateCreditEnd] = !!_modelDto[projectDto.dateCreditEnd] ? new Date(_modelDto[projectDto.dateCreditEnd]!) : null;
            this[projectData.dateCloseDay] = _modelDto[projectDto.dateCloseDay] ?? null;
            this[projectData.dateClose] = dayToLocalDate(this[projectData.dateCloseDay]);
            this[projectData.countryCode] = _modelDto[projectDto.countryCode] ?? null;
            this[projectData.country] = this[projectData.countryCode]
                ? { itemId: this[projectData.countryCode]!, itemText: this[projectData.countryCode]! }
                : null;
            this[projectData.timeZoneId] = _modelDto[projectDto.timeZoneId] ?? null;
            this[projectData.deleted] = _modelDto[projectDto.deleted]!;
            this[projectData.inactive] = _modelDto[projectDto.inactive]!;

            this[projectData.customer_description] = _modelDto[projectDto.customer_Description] ?? null;
            this[projectData.projectCategory_description] = _modelDto[projectDto.projectCategoryDto]?.[projectCategoryDto.description] ?? null;
            this[projectData.projectCategory_guid] = _modelDto[projectDto.projectCategory_Guid] ?? null;
            this[projectData.customer_guid] = _modelDto[projectDto.customer_Guid] ?? null;
            // Het klantveld in Edit toont bij een gekoppelde klant diens naam (readonly); de backend negeert
            // de omschrijving zodra er een klant gekoppeld is.
            this[projectData.customerDescription] = _modelDto[projectDto.customer_Guid]
                ? _modelDto[projectDto.customer_Description] ?? null
                : _modelDto[projectDto.customerDescription] ?? null;
            this[projectData.address] = _modelDto[projectDto.address] ?? null;
            this[projectData.streetNr] = _modelDto[projectDto.streetNr] ?? null;
            this[projectData.postalCode] = _modelDto[projectDto.postalCode] ?? null;
            this[projectData.town] = _modelDto[projectDto.town] ?? null;
            this[projectData.county] = _modelDto[projectDto.county] ?? null;

            this[projectData.photoThumbGuid] = _modelDto[projectDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.guid] ?? null;
            this[projectData.photoOriginGuid] = _modelDto[projectDto.photoBoxDto]?.[imageBoxDto.photoDto]?.[imageDto.guid] ?? null;
            this[projectData.photoVersion] = _modelDto[projectDto.photoBoxDto]?.[imageBoxDto.photoThumbDto]?.[imageDto.modifiedOn] ?? null;

            this[projectData.photoBoxData] = ImageBoxData.create(_modelDto[projectDto.photoBoxDto]) ?? null;
            this[projectData.customerData] = ContactData.create(_modelDto[projectDto.customerDto]) ?? null;
            this[projectData.projectCategoryData] = ProjectCategoryData.create(_modelDto[projectDto.projectCategoryDto]) ?? null;
        }
    }
        
    static create(modelDto?: IProjectDto | null): IProjectData | null
    static create(modelData: IProjectData, modelForm: IProjectForm): IProjectData | null
    static create(value?: IProjectDto | IProjectData | null, modelForm?: IProjectForm): IProjectData | null {

        if (!!modelForm) {
            const _modelData = value as IProjectData;
            return new ProjectData(_modelData, modelForm);
        } else {
            const _modelDto = value as IProjectDto;
            return _modelDto ? new ProjectData(_modelDto) : null;
        }
    }
}
